import * as React from 'react'
import { HeartIcon } from '@heroicons/react/24/solid'
import { RemoteResource, getResourceUrl } from '../../services/api'

interface PhotoTileProps {
  photo: RemoteResource
  width: number
  height: number
  /** takes the id so tiles can share one stable callback and stay memoized */
  onPhotoClick?: (photoId: string) => void
}

const formatDuration = (durationMs: number): string => {
  const totalSeconds = Math.round(durationMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export const PhotoTile: React.FC<PhotoTileProps> = React.memo(
  ({ photo, width, height, onPhotoClick }) => {
    const [isLoaded, setIsLoaded] = React.useState(false)
    const imgRef = React.useRef<HTMLImageElement>(null)
    const imageUrl = getResourceUrl(photo, 'small')

    // onLoad can fire before React attaches the listener for cached images
    React.useEffect(() => {
      if (imgRef.current?.complete) setIsLoaded(true)
    }, [imageUrl])

    return (
      <button
        type="button"
        onClick={onPhotoClick ? () => onPhotoClick(photo.id) : undefined}
        style={{ width, height }}
        className="relative block shrink-0 cursor-pointer overflow-hidden bg-gray-200 transition duration-200 ease-out md:hover:z-10 md:hover:scale-[1.02] md:hover:shadow-[0_0_12px_rgba(0,0,0,0.08)] md:focus-visible:scale-[1.02]"
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt={photo.name}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
        {photo.type === 'video' && photo.duration != null && (
          <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-xs text-white">
            {formatDuration(photo.duration)}
          </span>
        )}
        {photo.is_favorite && (
          <HeartIcon className="absolute right-1 top-1 h-4 w-4 text-white drop-shadow" />
        )}
      </button>
    )
  },
  (prev, next) =>
    prev.photo.id === next.photo.id &&
    prev.width === next.width &&
    prev.height === next.height &&
    prev.onPhotoClick === next.onPhotoClick
)
